/**
 * upgrade-backup.ts — the undo log of `ss upgrade`.
 *
 * Every file the upgrade touches is copied BEFORE the change into
 * `.smartstack/upgrade-backups/<ISO>/files/<rel>`, and described in
 * `manifest.json` (`existed`, `sha256Before`, `sha256After`). It replaces the
 * scattered `.bak` siblings the codemods used to drop next to the sources.
 *
 * Two ways in:
 * - `write(rel, content)` — the upgrade writes the file itself;
 * - `capture(paths)` — an EXTERNAL writer is about to touch them (`dotnet add`,
 *   `npm install`, a child scaffolder CLI): the before-state is recorded now,
 *   the after-state is read from disk at `finalize()`.
 *
 * The set is LAZY: nothing exists on disk until the first capture, so a
 * preflight that aborts — or an upgrade with nothing to change — leaves no
 * backup folder behind. The manifest is rewritten after every capture, so a
 * crashed upgrade still leaves a restorable log (`finalizedAt: null`).
 *
 * `restore(id)` puts every file back, under two safety rules:
 * - a file the upgrade CREATED is deleted only if its hash still matches what
 *   the upgrade wrote (a file edited since then is kept and reported);
 * - a file edited AFTER the upgrade (its hash no longer matches `sha256After`)
 *   is not overwritten either: it is reported as a conflict with the path of
 *   its backup copy. Restoring never destroys work done after the upgrade.
 *
 * The backup folder ignores itself (`.gitignore` = `*`): it may hold secrets
 * (docker-images/.env) and must never be committed, even in a project whose
 * root .gitignore predates the `.smartstack/upgrade-backups/` entry.
 */
import { createHash } from 'crypto';
import fs from 'fs-extra';
import { dirname, isAbsolute, join, relative, resolve, sep } from 'path';

export const UPGRADE_BACKUPS_REL = '.smartstack/upgrade-backups';

export interface VersionTriple {
  /** NuGet `SmartStack` version. */
  socle: string | null;
  /** npm `@atlashub/smartstack` version. */
  npm: string | null;
  /** `@atlashub/smartstack-cli` version. */
  cli: string | null;
}

export interface BackupFileEntry {
  /** Path relative to the project root, forward slashes. */
  rel: string;
  /** The file existed before the upgrade (else the upgrade created it). */
  existed: boolean;
  sha256Before: string | null;
  /** Hash after the upgrade; null = absent after the upgrade (or unknown while the set is open). */
  sha256After: string | null;
}

export interface BackupManifest {
  schemaVersion: 1;
  id: string;
  createdAt: string;
  /** Null while the upgrade runs (or if it crashed) — after-hashes are then incomplete. */
  finalizedAt: string | null;
  from: VersionTriple;
  to: VersionTriple;
  gitHead: string | null;
  branch: string | null;
  files: BackupFileEntry[];
  restoredAt?: string;
}

export interface BackupMeta {
  from: VersionTriple;
  to: VersionTriple;
  gitHead: string | null;
  branch: string | null;
}

export interface RestoreConflict {
  rel: string;
  reason: string;
  /** Absolute path of the pre-upgrade copy, when there is one. */
  backupCopy: string | null;
}

export interface RestoreReport {
  id: string;
  restored: string[];
  deleted: string[];
  unchanged: string[];
  conflicts: RestoreConflict[];
}

export function sha256(content: string | Buffer): string {
  return createHash('sha256').update(content).digest('hex');
}

async function hashOf(path: string): Promise<string | null> {
  try {
    return sha256(await fs.readFile(path));
  } catch {
    return null;
  }
}

/** Windows-safe, lexically sortable id: `2026-10-21T14-03-22-123Z`. */
export function backupIdFor(date: Date): string {
  return date.toISOString().replace(/[:.]/g, '-');
}

export function backupsRoot(projectDir: string): string {
  return join(projectDir, ...UPGRADE_BACKUPS_REL.split('/'));
}

export class BackupSet {
  private readonly entries = new Map<string, BackupFileEntry>();
  private materialized = false;

  private constructor(
    readonly projectDir: string,
    private readonly manifest: BackupManifest,
    readonly dryRun: boolean,
  ) {}

  /** A new, still virtual, backup set. Nothing is written until the first capture. */
  static open(projectDir: string, meta: BackupMeta, opts: { now?: Date; dryRun?: boolean } = {}): BackupSet {
    const now = opts.now ?? new Date();
    const manifest: BackupManifest = {
      schemaVersion: 1,
      id: backupIdFor(now),
      createdAt: now.toISOString(),
      finalizedAt: null,
      from: meta.from,
      to: meta.to,
      gitHead: meta.gitHead,
      branch: meta.branch,
      files: [],
    };
    return new BackupSet(resolve(projectDir), manifest, opts.dryRun ?? false);
  }

  get id(): string {
    return this.manifest.id;
  }

  get dir(): string {
    return join(backupsRoot(this.projectDir), this.manifest.id);
  }

  /** At least one file is recorded. */
  get hasFiles(): boolean {
    return this.entries.size > 0;
  }

  /** Normalise a path (absolute under the project, or relative) to the manifest form. Throws on an escape. */
  relOf(path: string): string {
    const abs = isAbsolute(path) ? resolve(path) : resolve(this.projectDir, path);
    const rel = relative(this.projectDir, abs);
    if (!rel || rel.startsWith('..') || isAbsolute(rel)) {
      throw new Error(`upgrade-backup: ${path} is outside the project ${this.projectDir}`);
    }
    return rel.split(sep).join('/');
  }

  private absOf(rel: string): string {
    return join(this.projectDir, ...rel.split('/'));
  }

  private copyOf(rel: string): string {
    return join(this.dir, 'files', ...rel.split('/'));
  }

  isCaptured(path: string): boolean {
    return this.entries.has(this.relOf(path));
  }

  private async materialize(): Promise<void> {
    if (this.materialized) return;
    const root = backupsRoot(this.projectDir);
    await fs.ensureDir(this.dir);
    const selfIgnore = join(root, '.gitignore');
    if (!(await fs.pathExists(selfIgnore))) {
      await fs.writeFile(selfIgnore, '# ss upgrade backups — may hold secrets, never versioned\n*\n', 'utf-8');
    }
    this.materialized = true;
  }

  private async persist(): Promise<void> {
    this.manifest.files = [...this.entries.values()].sort((a, b) => a.rel.localeCompare(b.rel));
    await fs.writeFile(join(this.dir, 'manifest.json'), JSON.stringify(this.manifest, null, 2) + '\n', 'utf-8');
  }

  /**
   * Record the before-state of files about to be changed by someone else. The
   * FIRST capture of a path wins (it is the pre-upgrade state); later captures
   * of the same path are no-ops. Missing files are recorded as `existed:false`.
   */
  async capture(paths: string[]): Promise<void> {
    if (this.dryRun) return;
    let added = false;
    for (const path of paths) {
      const rel = this.relOf(path);
      if (this.entries.has(rel)) continue;
      const abs = this.absOf(rel);
      const exists = (await fs.pathExists(abs)) && (await fs.stat(abs)).isFile();
      await this.materialize();
      if (exists) {
        await fs.ensureDir(dirname(this.copyOf(rel)));
        await fs.copyFile(abs, this.copyOf(rel));
      }
      this.entries.set(rel, {
        rel,
        existed: exists,
        sha256Before: exists ? await hashOf(abs) : null,
        sha256After: null,
      });
      added = true;
    }
    if (added) await this.persist();
  }

  /** Write a file the upgrade owns the change of — captured first. */
  async write(path: string, content: string | Buffer): Promise<void> {
    if (this.dryRun) return;
    const rel = this.relOf(path);
    await this.capture([rel]);
    const abs = this.absOf(rel);
    await fs.ensureDir(dirname(abs));
    await fs.writeFile(abs, content);
    this.entries.get(rel)!.sha256After = sha256(typeof content === 'string' ? Buffer.from(content, 'utf-8') : content);
    await this.persist();
  }

  /** Delete a file — captured first, so a restore brings it back. */
  async remove(path: string): Promise<void> {
    if (this.dryRun) return;
    const rel = this.relOf(path);
    await this.capture([rel]);
    await fs.remove(this.absOf(rel));
    this.entries.get(rel)!.sha256After = null;
    await this.persist();
  }

  /**
   * Read every recorded file back from disk: the after-hash becomes
   * authoritative, files the upgrade left identical are dropped (with their
   * copy). Returns the manifest, or null when nothing changed — the backup
   * folder is then removed entirely.
   */
  async finalize(): Promise<BackupManifest | null> {
    if (this.dryRun || !this.materialized) return null;
    for (const [rel, entry] of [...this.entries]) {
      const after = await hashOf(this.absOf(rel));
      const unchanged = entry.existed ? after === entry.sha256Before : after === null;
      if (unchanged) {
        this.entries.delete(rel);
        await fs.remove(this.copyOf(rel));
        continue;
      }
      entry.sha256After = after;
    }
    if (this.entries.size === 0) {
      await fs.remove(this.dir);
      return null;
    }
    this.manifest.finalizedAt = new Date().toISOString();
    await this.persist();
    return { ...this.manifest, files: [...this.manifest.files] };
  }

  /** Every backup of the project, newest first. Unreadable manifests are skipped. */
  static async list(projectDir: string): Promise<BackupManifest[]> {
    const root = backupsRoot(resolve(projectDir));
    if (!(await fs.pathExists(root))) return [];
    const out: BackupManifest[] = [];
    for (const entry of await fs.readdir(root, { withFileTypes: true })) {
      if (!entry.isDirectory()) continue;
      try {
        const manifest = JSON.parse(await fs.readFile(join(root, entry.name, 'manifest.json'), 'utf-8')) as BackupManifest;
        if (manifest && Array.isArray(manifest.files)) out.push(manifest);
      } catch {
        /* not a backup folder */
      }
    }
    return out.sort((a, b) => b.id.localeCompare(a.id));
  }

  /**
   * Put the files of backup `id` (or the newest, `'latest'`) back. Never
   * destroys work done after the upgrade — see the module header.
   */
  static async restore(projectDir: string, id: string): Promise<RestoreReport> {
    const project = resolve(projectDir);
    const all = await BackupSet.list(project);
    const manifest = id === 'latest' ? all[0] : all.find((m) => m.id === id);
    if (!manifest) {
      throw new Error(
        all.length === 0
          ? 'No upgrade backup found in .smartstack/upgrade-backups/.'
          : `No upgrade backup "${id}" — available: ${all.map((m) => m.id).join(', ')}`,
      );
    }
    const dir = join(backupsRoot(project), manifest.id);
    const report: RestoreReport = { id: manifest.id, restored: [], deleted: [], unchanged: [], conflicts: [] };
    const final = manifest.finalizedAt !== null;

    for (const entry of manifest.files) {
      const abs = join(project, ...entry.rel.split('/'));
      const copy = join(dir, 'files', ...entry.rel.split('/'));
      const current = await hashOf(abs);

      if (entry.existed) {
        if (current === entry.sha256Before) {
          report.unchanged.push(entry.rel);
          continue;
        }
        if (final && current !== entry.sha256After) {
          report.conflicts.push({ rel: entry.rel, reason: 'modified after the upgrade — not overwritten', backupCopy: copy });
          continue;
        }
        if (!(await fs.pathExists(copy))) {
          report.conflicts.push({ rel: entry.rel, reason: 'backup copy missing', backupCopy: null });
          continue;
        }
        await fs.ensureDir(dirname(abs));
        await fs.copyFile(copy, abs);
        report.restored.push(entry.rel);
        continue;
      }

      // Created by the upgrade.
      if (current === null) {
        report.unchanged.push(entry.rel);
        continue;
      }
      if (entry.sha256After !== null && current === entry.sha256After) {
        await fs.remove(abs);
        await removeEmptyParents(dirname(abs), project);
        report.deleted.push(entry.rel);
        continue;
      }
      report.conflicts.push({
        rel: entry.rel,
        reason: entry.sha256After === null
          ? 'created by the upgrade, content unknown (the upgrade did not finish) — kept'
          : 'created by the upgrade, then modified — kept',
        backupCopy: null,
      });
    }

    const stamped: BackupManifest = { ...manifest, restoredAt: new Date().toISOString() };
    await fs.writeFile(join(dir, 'manifest.json'), JSON.stringify(stamped, null, 2) + '\n', 'utf-8');
    return report;
  }
}

/** Remove now-empty folders from `dir` up to (excluding) `stop`. */
async function removeEmptyParents(dir: string, stop: string): Promise<void> {
  let current = dir;
  while (current.startsWith(stop) && current !== stop) {
    try {
      if ((await fs.readdir(current)).length > 0) return;
      await fs.rmdir(current);
    } catch {
      return;
    }
    current = dirname(current);
  }
}
